import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { Snackbar, Alert, AlertTitle, Button, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { useNavigate } from 'react-router-dom';

export type NotificationType = 'success' | 'info' | 'warning' | 'error';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  patientId?: string;
  patientName?: string;
  referralId?: string;
  autoHideDuration?: number;
  icon?: React.ReactNode;
}

interface NotificationContextType {
  showNotification: (notification: Omit<Notification, 'id'>) => void;
  showPatientChangeNotification: (
    patientId: string,
    patientName: string,
    changeType: 'upgrade' | 'deterioration' | 'reevaluation' | 'urgent',
    message: string
  ) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const navigate = useNavigate();

  const showNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = `notification-${Date.now()}-${Math.random()}`;
    const newNotification: Notification = {
      id,
      autoHideDuration: 6000, // Default 6 seconds
      ...notification,
    };

    setNotifications(prev => [...prev, newNotification]);

    // If no notification is currently showing, show this one immediately
    if (!currentNotification) {
      setCurrentNotification(newNotification);
    }
  }, [currentNotification]);

  const showPatientChangeNotification = useCallback((
    patientId: string,
    patientName: string,
    changeType: 'upgrade' | 'deterioration' | 'reevaluation' | 'urgent',
    message: string
  ) => {
    const config = {
      upgrade: {
        type: 'success' as NotificationType,
        title: 'Patient Status Upgraded',
        icon: <TrendingUpIcon />,
      },
      deterioration: {
        type: 'warning' as NotificationType,
        title: 'Clinical Deterioration',
        icon: <TrendingDownIcon />,
      },
      reevaluation: {
        type: 'info' as NotificationType,
        title: 'Patient Re-evaluated',
        icon: <NotificationsActiveIcon />,
      },
      urgent: {
        type: 'error' as NotificationType,
        title: 'Urgent Attention Required',
        icon: <PriorityHighIcon />,
      },
    };

    const { type, title, icon } = config[changeType];

    showNotification({
      type,
      title,
      message,
      patientId,
      patientName,
      icon,
      autoHideDuration: 8000, // 8 seconds for patient changes
    });
  }, [showNotification]);

  const handleClose = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
    // Don't close on clickaway - only close on timeout or explicit close button
    if (reason === 'clickaway') {
      return;
    }

    setCurrentNotification(null);

    // Show next notification after a brief delay
    setTimeout(() => {
      setNotifications(prev => {
        const remaining = prev.filter(n => n.id !== currentNotification?.id);
        if (remaining.length > 0) {
          setCurrentNotification(remaining[0]);
        }
        return remaining;
      });
    }, 300);
  }, [currentNotification]);

  const handleViewPatient = useCallback(() => {
    if (currentNotification?.patientId) {
      navigate(`/predict/patient/${currentNotification.patientId}`);
      handleClose();
    }
  }, [currentNotification, navigate, handleClose]);

  const handleViewReferral = useCallback(() => {
    if (currentNotification?.referralId) {
      navigate(`/transition/referral/${currentNotification.referralId}`);
      handleClose();
    }
  }, [currentNotification, navigate, handleClose]);

  const contextValue: NotificationContextType = {
    showNotification,
    showPatientChangeNotification,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={!!currentNotification}
        autoHideDuration={currentNotification?.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ maxWidth: 500 }}
      >
        {currentNotification ? (
          <Alert
            severity={currentNotification.type}
            icon={currentNotification.icon || undefined}
            sx={{
              width: '100%',
              alignItems: 'flex-start',
              boxShadow: 3,
            }}
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {currentNotification.patientId && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleViewPatient}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View Patient
                  </Button>
                )}
                {currentNotification.referralId && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleViewReferral}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View Referral
                  </Button>
                )}
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={handleClose}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
              {currentNotification.title}
            </AlertTitle>
            {currentNotification.patientName && (
              <Box component="span" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                {currentNotification.patientName}
              </Box>
            )}
            {currentNotification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationContext.Provider>
  );
};
